/**
 * insertionOverlap — P7 overlap warning utility.
 *
 * When a user commits a block INSERT at a point that overlaps the
 * bounding box of one or more existing entities in the active space,
 * the UX surfaces a confirmation dialog ("Continue?"). This module
 * computes that overlap report deterministically from the draft
 * insertion point, the block's own authoring-space bounds and the
 * list of existing entities.
 *
 * Behaviour:
 * - AABB intersection uses INCLUSIVE edges (touching-on-an-edge counts
 *   as an overlap) — this matches AutoCAD's "touches or overlaps" rule
 *   for the WSELECT crossing window and similar hit tests.
 * - Entities without a computable bounding box (getEntityBounds returns
 *   null) are skipped.
 * - Only entities in the active space are considered; the caller is
 *   responsible for pre-filtering the `existingEntities` list.
 * - When the insertion AABB is degenerate (block has no geometry), we
 *   treat the insertion as a single point and test point-in-bbox for
 *   each existing entity.
 *
 * D1(a2) S12 — bounds of each EXISTING entity are resolved via
 * `@dxf-ai-editor/cad-core`'s typed `getEntityBounds` (reading the
 * canonical `DrawingEntity` fields directly) rather than the legacy
 * `./entityBounds.ts` string-bag path. This is a strict coverage
 * improvement for the overlap check: notably, an existing BLOCK
 * (INSERT) entity now overlap-tests against its REAL resolved
 * footprint (when `ctx.blockDefinitions` is supplied) instead of the
 * legacy path's hardcoded 80×48 "icon" box, and every kind cad-core
 * supports (Dimension / Multileader / Revision Cloud / Wipeout / Hatch
 * boundary / Viewport / Rectangular Associative Array) now contributes
 * a real bounds instead of being silently skipped.
 */

import type { DimensionStyle, DrawingEntity } from "@dxf-ai-editor/drawing-model";
import { getEntityBounds, type BlockLookupContext } from "@dxf-ai-editor/cad-core";
import { pointBox, specializeBlockInstance } from "./blockInstanceResolve";
import { applyDimensionStyleOverrides } from "./dimensionHelpers";

export interface InsertionBounds {
  /** Block-local bounds relative to (0,0). */
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface OverlapInfo {
  /** Combined bounding box covering every overlapping entity + the
   * insertion footprint itself, useful for "region (L..R, T..B)" label. */
  combined: { left: number; right: number; top: number; bottom: number };
  /** IDs of existing entities whose bboxes intersect the insertion bbox. */
  overlappingIds: string[];
}

/**
 * Translate block-local bounds to world-space via the insertion point
 * and uniform or non-uniform scale factors. Rotation is ignored for
 * this AABB approximation — the result is a conservative axis-aligned
 * bounding envelope sufficient for an overlap warning (full parity
 * would require rotating the four corners and re-AABB'ing, which is
 * excessive for a UX confirm prompt).
 */
export function insertionFootprint(
  insertionPoint: { x: number; y: number },
  blockBounds: InsertionBounds,
  scaleX = 1,
  scaleY = 1,
): { left: number; right: number; top: number; bottom: number } {
  const sx = Math.abs(scaleX) || 1;
  const sy = Math.abs(scaleY) || 1;
  const left = insertionPoint.x + blockBounds.minX * sx;
  const right = insertionPoint.x + blockBounds.maxX * sx;
  const top = insertionPoint.y + blockBounds.minY * sy;
  const bottom = insertionPoint.y + blockBounds.maxY * sy;
  return {
    left: Math.min(left, right),
    right: Math.max(left, right),
    top: Math.min(top, bottom),
    bottom: Math.max(top, bottom),
  };
}

/**
 * Inclusive AABB intersection predicate. Returns true when the two
 * rectangles share at least one point (including edges / corners).
 */
function aabbIntersects(
  a: { left: number; right: number; top: number; bottom: number },
  b: { left: number; right: number; top: number; bottom: number },
): boolean {
  return !(
    a.right < b.left ||
    a.left > b.right ||
    a.bottom < b.top ||
    a.top > b.bottom
  );
}

/**
 * codex PR #472 finding 3 (round 1) / round-2 finding 1 / round-3 findings
 * 2 + 5 — resolve an EXISTING entity's bounds for the overlap test,
 * evaluating dynamic-block state for the top-level INSERT *and* every nested
 * dynamic `Block` descendant PER INSTANCE before handing the block-definitions
 * map to cad-core.
 *
 * `getEntityBounds`'s own `Block` case resolves the RAW block definition's
 * child geometry via `ctx.blockDefinitions` (keyed by block NAME) — it has no
 * notion of a placed INSERT's own `customProperties` / `visibilityState`
 * (DYN-1.5) at ANY nesting depth. The render path DOES evaluate this before
 * drawing (`blockRender.tsx`'s `getCachedEvaluation` → `evaluateBlock` for the
 * top-level instance, plus the same call for every nested `Block` child): a
 * visibility state that hides a far-off child, or a parameter that stretches
 * geometry, changes what's actually ON SCREEN, so testing the overlap
 * footprint against the un-evaluated definition can both false-positive (warn
 * about geometry that's actually hidden) and false-negative (miss a
 * parameter-stretched child that now reaches the new INSERT's footprint).
 *
 * A NAME-keyed override map cannot represent two nested INSERTs of the SAME
 * dynamic block carrying DIFFERENT state (round-3 finding 2 — the first
 * evaluated instance would win for every later reference). `specializeBlockInstance`
 * instead rewrites the tree so every nested INSERT reference points at a
 * UNIQUELY-named synthetic definition keyed by its recursion PATH, each
 * pre-evaluated with that instance's own state (round-3 finding 5), with
 * insertion-point markers for unresolved references (round-3 finding 3).
 *
 * `entity.customProperties` / `entity.visibilityState` are typed fields on
 * `BlockInsertEntity` (no `EntityPreview.properties` string-bag round-trip
 * needed, unlike the render path's `readInstanceOverrides` — the typed
 * model already carries this DYN-1 state directly).
 *
 * codex PR #472 round-6 finding — a top-level EXISTING `Text` entity is
 * framed at its insertion POINT ONLY, never unioned with `alignmentPoint`.
 * Every renderer that can draw a top-level TEXT anchors its glyphs AT
 * `insertionPoint` and never reads the `alignmentPoint` COORDINATES —
 * `textFromEntity.ts` (the live scene3d adapter) derives only a boolean
 * `hasAlignmentPoint` gate from it (to pick a justification mode), feeding
 * the SAME `insRaw = entity.insertionPoint` to the geometry builder; the
 * legacy overlap path's own `entityBounds.ts` Text case likewise reads only
 * `Insertion X/Y`. cad-core's default dispatch for `Text` (the
 * `entityModelBounds` case reused from `bounds.ts`'s fallthrough branch)
 * instead unions `insertionPoint ∪ alignmentPoint`, so an existing TEXT
 * authored (or DXF-imported) with a far-off alignment point — common when a
 * justification is set but the glyph anchor itself never moved — inflated
 * the overlap AABB into empty space no renderer ever fills, opening a false
 * "Continue?" warning for a block placed only near the unused alignment
 * point. This is the SAME renderer-parity treatment already applied to
 * in-block-preview Text bounds (round-4 finding 1 — see
 * `blockInstanceResolve.ts::previewLeafLocalBounds`'s Text/MText/Attdef
 * case) — reusing that module's exact `pointBox` primitive rather than a
 * second box-literal copy (one truth).
 */
function resolveEntityBoundsForOverlap(
  entity: DrawingEntity,
  ctx?: BlockLookupContext,
  dimStylesByName?: ReadonlyMap<string, DimensionStyle>,
): ReturnType<typeof getEntityBounds> {
  if (entity.type === "Text") {
    return pointBox(entity.insertionPoint);
  }
  if (entity.type !== "Block" || !ctx?.blockDefinitions) {
    return getEntityBounds(entity, ctx);
  }
  const spec = specializeBlockInstance(
    entity.blockName,
    {
      customProperties: entity.customProperties,
      visibilityState: entity.visibilityState,
    },
    ctx.blockDefinitions,
    // codex PR #472 round-5 finding 1 — resolve DIMENSION children nested
    // inside this existing INSERT's (raw `geometryDocument.blocks`) definition
    // against the DIMSTYLE table WHILE specializing, so their style-driven
    // extension / text padding is framed instead of cad-core's hardcoded
    // defaults (the top-level `resolveDimensionStyleBounds` normalization never
    // reaches entities buried inside a block definition).
    dimStylesByName,
  );
  if (!spec) {
    return getEntityBounds(entity, ctx);
  }
  // Re-point the INSERT at the specialized (per-instance) root definition and
  // resolve against the self-contained specialized table.
  return getEntityBounds(
    { ...entity, blockName: spec.rootName },
    { blockDefinitions: spec.definitions },
  );
}

/**
 * Fold each existing DIMENSION's referenced DIMSTYLE table values into its
 * typed `styleOverrides` for the four DIMVARs cad-core's `dimensionBounds`
 * reads (DIMEXE / DIMEXO / DIMGAP / DIMTXT) — codex PR #472 round-4 finding 4.
 *
 * cad-core's `dimensionBounds` reads `e.styleOverrides.dimexe ?? 1.25` (etc.):
 * a per-entity DSTYLE override wins, but when ABSENT it falls to a HARDCODED
 * default — it never consults the named DIMSTYLE table. So a DIMENSION whose
 * DIMSTYLE supplies a LARGER extension-line overshoot (DIMEXE) or text gap
 * (DIMGAP) than the default has that extra RENDERED padding dropped from the
 * overlap footprint, and an INSERT overlapping ONLY that style-driven padding
 * zone passes with no warning. This resolves the SAME `DimensionStyle` table
 * the renderer uses (`resolveDimensionStyleRow` + `dimensionStyleRowToValues`,
 * the one-truth relocated to `dimensionHelpers.ts`) and materialises the
 * EFFECTIVE value onto `styleOverrides`, so the raw typed entities the overlap
 * path reads carry the same style enrichment the render path already applies:
 *
 *   - dimexe / dimexo / dimgap → per-entity override ?? DIMSTYLE-table value.
 *   - dimtxt → per-entity override ?? (entity.textHeight when positive, else
 *     the DIMSTYLE-table DIMTXT) — mirrors the renderer's
 *     `positiveOr(textHeightRaw, style.dimtxt)` effective-height rule AND
 *     cad-core's own `?? (textHeight > 0 ? textHeight : …)` fallback, only
 *     substituting the STYLE value (not the 2.5 hardcode) when neither is set.
 *
 * Non-DIMENSION entities pass through by reference; DIMENSION entities are
 * copied (never mutated). With no matching style the resolution yields
 * `defaultDimStyle`, so a `?? default`-only document is byte-identical to the
 * prior behaviour (no regression when there is no enriching style).
 */
export function resolveDimensionStyleBounds(
  entities: readonly DrawingEntity[],
  dimStylesByName: ReadonlyMap<string, DimensionStyle>,
): DrawingEntity[] {
  // Per-entity resolution is the one-truth `applyDimensionStyleOverrides`
  // (`dimensionHelpers.ts`), shared verbatim with the block-definition
  // specialization path (`blockInstanceResolve.ts::specializeDefinition`) so a
  // DIMENSION nested inside a block resolves the SAME DIMVARs as a top-level
  // one (codex PR #472 round-5 finding 1).
  return entities.map((entity) =>
    applyDimensionStyleOverrides(entity, dimStylesByName),
  );
}

/**
 * Detect overlap between the block's insertion footprint and any
 * existing entity's bounding box.
 *
 * `ctx` (optional) resolves an existing BLOCK (INSERT) entity's REAL
 * transformed footprint via cad-core's block-transform-chain resolver;
 * without it a Block falls back to its insertion-point-only degenerate
 * box (see `getEntityBounds`'s doc).
 *
 * Top-level `existingEntities` are expected to already carry style-resolved
 * DIMENSION `styleOverrides` (see {@link resolveDimensionStyleBounds}) — the P7
 * hook runs that normalization first so a DIMSTYLE-driven padding zone is not
 * dropped. `dimStylesByName` (optional) additionally resolves DIMENSION
 * children NESTED inside an existing Block INSERT while its per-instance table
 * is specialized, so a dimension buried in a block frames the same style-driven
 * padding (codex PR #472 round-5 finding 1).
 *
 * @returns OverlapInfo describing the combined region and the list of
 *   overlapping entity IDs, or null when nothing overlaps.
 */
export function detectInsertionOverlap(
  insertionPoint: { x: number; y: number },
  blockBounds: InsertionBounds,
  existingEntities: readonly DrawingEntity[],
  scaleX = 1,
  scaleY = 1,
  ctx?: BlockLookupContext,
  dimStylesByName?: ReadonlyMap<string, DimensionStyle>,
): OverlapInfo | null {
  if (!Number.isFinite(insertionPoint.x) || !Number.isFinite(insertionPoint.y)) {
    return null;
  }
  if (existingEntities.length === 0) {
    return null;
  }

  const footprint = insertionFootprint(
    insertionPoint,
    blockBounds,
    scaleX,
    scaleY,
  );

  const overlappingIds: string[] = [];
  let combinedLeft = footprint.left;
  let combinedRight = footprint.right;
  let combinedTop = footprint.top;
  let combinedBottom = footprint.bottom;

  for (const entity of existingEntities) {
    const bounds = resolveEntityBoundsForOverlap(entity, ctx, dimStylesByName);
    if (!bounds) {
      continue;
    }
    // cad-core's `EntityBounds` is `{minX,minY,maxX,maxY}` in WCS (Y-up,
    // no display flip) — `minY`/`maxY` map directly to our footprint's
    // `top`/`bottom` (both conventions are "the smaller Y value" /
    // "the larger Y value"), so the predicate is direct.
    if (
      aabbIntersects(
        {
          left: bounds.minX,
          right: bounds.maxX,
          top: bounds.minY,
          bottom: bounds.maxY,
        },
        footprint,
      )
    ) {
      overlappingIds.push(entity.id);
      if (bounds.minX < combinedLeft) combinedLeft = bounds.minX;
      if (bounds.maxX > combinedRight) combinedRight = bounds.maxX;
      if (bounds.minY < combinedTop) combinedTop = bounds.minY;
      if (bounds.maxY > combinedBottom) combinedBottom = bounds.maxY;
    }
  }

  if (overlappingIds.length === 0) {
    return null;
  }

  return {
    combined: {
      left: combinedLeft,
      right: combinedRight,
      top: combinedTop,
      bottom: combinedBottom,
    },
    overlappingIds,
  };
}
